import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Auto-logout hook with inactivity detection
 * 
 * Features:
 * - Tracks mouse, keyboard, touch, and scroll activity
 * - Shows warning dialog before logout
 * - Syncs logout across multiple tabs
 * - Configurable timeout and warning duration
 * 
 * @param {Object} options Configuration options
 * @param {number} options.timeout - Inactivity timeout in milliseconds (default: 60000 = 60 seconds)
 * @param {number} options.warningTime - Time to show warning before logout in ms (default: 10000 = 10 seconds)
 * @param {Function} options.onLogout - Custom logout function (optional)
 * @param {boolean} options.enabled - Enable/disable auto-logout (default: true)
 */
const useAutoLogout = ({
  timeout = 60000, // 60 seconds default
  warningTime = 10000, // 10 seconds warning
  onLogout = null,
  enabled = true
} = {}) => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownIntervalRef = useRef(null);

  // Default logout function
  const defaultLogout = useCallback(() => {
    try {
      // Clear auth/session storage only (preserve drafts/local data)
      localStorage.removeItem('adminToken');
      localStorage.removeItem('user');
      localStorage.removeItem('count');
      localStorage.removeItem('auto_logout_activity');
      
      // Broadcast logout to other tabs
      localStorage.setItem('logout-event', Date.now().toString());
      
      // Navigate to login
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [navigate]);

  const logout = onLogout || defaultLogout;

  // Reset all timers and hide warning
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Hide warning
    setShowWarning(false);
    setCountdown(0);

    if (!enabled) return;

    // Set warning timeout (shows dialog before logout)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      const warningSeconds = warningTime / 1000;
      setCountdown(warningSeconds);

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeout - warningTime);

    // Set final logout timeout
    timeoutRef.current = setTimeout(() => {
      logout();
    }, timeout);
  }, [enabled, timeout, warningTime, logout]);

  // Activity events to monitor
  const events = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];

  // Handle user activity
  const handleActivity = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Extend session (when user clicks "Stay Logged In")
  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Listen for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout-event') {
        // Another tab logged out - logout this tab too
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [logout]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      // Clear all timers if disabled
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      setShowWarning(false);
      return;
    }

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Start initial timer
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [enabled, handleActivity, resetTimers]);

  return {
    showWarning,
    countdown,
    extendSession,
    logout
  };
};

export default useAutoLogout;
